import { PRODUCT_TYPES, type ProductType } from '@mevaser/shared'
import { useSyncExternalStore } from 'react'

/**
 * The till's in-progress sale, kept in localStorage so a counter sale
 * survives a trip to Products/Stock/Provisioning and back — or a reload.
 * Same idiom as the product draft in routes/products.tsx: an envelope with a
 * version and a timestamp, sanitized field-by-field on read rather than
 * schema-parsed, and silently dropped once blank or stale.
 */

export interface OrderDraftLine {
  key: string
  productId: string
  sku: string
  name: string
  type: ProductType
  unitPriceCents: number
  qty: number
  serializedUnitId: string | null
  imei: string | null
  sourceLocationId: string | null
  sourceLocationName: string | null
  /** Null = non-taxable. Absent on a draft saved before t0947 — sanitized to
   *  null, same as any other field an old draft never had. */
  taxCategoryId: string | null
  /** For a plan line: the phone line it is for. Absent on a draft saved before
   *  plans could name one — sanitized to blank. */
  phoneLine?: string
  /** t1137: staff marked this line as possibly not the exact unit recorded. */
  unsure?: boolean
  /** Pasted IMEIs / ICCIDs / SKUs it might be; parsed on submit. */
  candidatesText?: string
  /** The batch / range it came out of. */
  pool?: string
}

export interface OrderDraftCustomer {
  id: string
  name: string
  businessName: string | null
  phone: string | null
  /** Absent on a pre-t0947 draft — sanitized to false, the same default a
   *  fresh customer lookup would show until proven otherwise. */
  taxExempt: boolean
}

/** One rental opened alongside the sale (t0943) — one product/unit, its own
 *  terms, matching how `NewRentalDialog` on the Rentals page creates one.
 *  Staff can add several of these to one cart, each with different terms. */
export interface OrderDraftRentalGroup {
  key: string
  productId: string | null
  productName: string
  productSku: string
  productType: ProductType | null
  unitId: string | null
  imei: string | null
  startDate: string
  dueDate: string
  /** Dollar-amount strings, same as `NewRentalDialog`'s own inputs — parsed
   *  to cents only at submit time. */
  feeCents: string
  billingInterval: string
  depositCents: string
  notes: string
}

export interface OrderDraftValues {
  lines: OrderDraftLine[]
  customer: OrderDraftCustomer | null
  locationId: string
  note: string
  payNow: boolean
  provider: string
  /** Absent on a draft saved before t0943 — sanitized to `[]`, same as any
   *  other field an old draft never had. */
  rentalGroups: OrderDraftRentalGroup[]
}

const ORDER_DRAFT_KEY = 'mv-order-draft'
const ORDER_DRAFT_VERSION = 1
const ORDER_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

function isBlankDraft(values: OrderDraftValues): boolean {
  return (
    values.lines.length === 0 &&
    values.rentalGroups.length === 0 &&
    !values.customer &&
    !values.note.trim()
  )
}

function sanitizeString(v: unknown): string | null {
  return typeof v === 'string' ? v : null
}

function sanitizeNullableString(v: unknown): string | null {
  return typeof v === 'string' ? v : null
}

function sanitizeLine(v: unknown): OrderDraftLine | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  const key = sanitizeString(o.key)
  const productId = sanitizeString(o.productId)
  const sku = sanitizeString(o.sku)
  const name = sanitizeString(o.name)
  const type =
    typeof o.type === 'string' && (PRODUCT_TYPES as readonly string[]).includes(o.type)
      ? (o.type as ProductType)
      : null
  const unitPriceCents = typeof o.unitPriceCents === 'number' ? o.unitPriceCents : null
  const qty =
    typeof o.qty === 'number' && Number.isFinite(o.qty) && o.qty > 0 ? Math.round(o.qty) : null
  if (!key || !productId || !sku || !name || !type || unitPriceCents === null || qty === null) {
    return null
  }
  return {
    key,
    productId,
    sku,
    name,
    type,
    unitPriceCents,
    qty,
    serializedUnitId: sanitizeNullableString(o.serializedUnitId),
    imei: sanitizeNullableString(o.imei),
    sourceLocationId: sanitizeNullableString(o.sourceLocationId),
    sourceLocationName: sanitizeNullableString(o.sourceLocationName),
    taxCategoryId: sanitizeNullableString(o.taxCategoryId),
    phoneLine: typeof o.phoneLine === 'string' ? o.phoneLine : '',
    unsure: o.unsure === true,
    candidatesText: typeof o.candidatesText === 'string' ? o.candidatesText : '',
    pool: typeof o.pool === 'string' ? o.pool : '',
  }
}

function sanitizeRentalGroup(v: unknown): OrderDraftRentalGroup | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  const key = sanitizeString(o.key)
  if (!key) return null
  const productType =
    typeof o.productType === 'string' &&
    (PRODUCT_TYPES as readonly string[]).includes(o.productType)
      ? (o.productType as ProductType)
      : null
  return {
    key,
    productId: sanitizeNullableString(o.productId),
    productName: typeof o.productName === 'string' ? o.productName : '',
    productSku: typeof o.productSku === 'string' ? o.productSku : '',
    productType,
    unitId: sanitizeNullableString(o.unitId),
    imei: sanitizeNullableString(o.imei),
    startDate: typeof o.startDate === 'string' ? o.startDate : '',
    dueDate: typeof o.dueDate === 'string' ? o.dueDate : '',
    feeCents: typeof o.feeCents === 'string' ? o.feeCents : '',
    billingInterval: typeof o.billingInterval === 'string' ? o.billingInterval : 'one_time',
    depositCents: typeof o.depositCents === 'string' ? o.depositCents : '',
    notes: typeof o.notes === 'string' ? o.notes : '',
  }
}

function sanitizeCustomer(v: unknown): OrderDraftCustomer | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  const id = sanitizeString(o.id)
  const name = sanitizeString(o.name)
  if (!id || !name) return null
  return {
    id,
    name,
    businessName: sanitizeNullableString(o.businessName),
    phone: sanitizeNullableString(o.phone),
    taxExempt: typeof o.taxExempt === 'boolean' ? o.taxExempt : false,
  }
}

function sanitizeValues(v: unknown): OrderDraftValues | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  const lines = Array.isArray(o.lines)
    ? o.lines.map(sanitizeLine).filter((l): l is OrderDraftLine => l !== null)
    : []
  const rentalGroups = Array.isArray(o.rentalGroups)
    ? o.rentalGroups.map(sanitizeRentalGroup).filter((g): g is OrderDraftRentalGroup => g !== null)
    : []
  return {
    lines,
    customer: sanitizeCustomer(o.customer),
    locationId: typeof o.locationId === 'string' ? o.locationId : '',
    note: typeof o.note === 'string' ? o.note : '',
    payNow: typeof o.payNow === 'boolean' ? o.payNow : true,
    provider: typeof o.provider === 'string' ? o.provider : 'cash',
    rentalGroups,
  }
}

export function readOrderDraft(): OrderDraftValues | null {
  try {
    const raw = window.localStorage.getItem(ORDER_DRAFT_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const obj = parsed as Record<string, unknown>
    if (obj.version !== ORDER_DRAFT_VERSION) return null
    if (typeof obj.savedAt !== 'number' || Date.now() - obj.savedAt > ORDER_DRAFT_MAX_AGE_MS) {
      return null
    }
    const values = sanitizeValues(obj.values)
    if (!values || isBlankDraft(values)) return null
    return values
  } catch {
    return null
  }
}

function countItems(values: OrderDraftValues | null): number {
  if (!values) return 0
  return values.lines.reduce((sum, l) => sum + l.qty, 0) + values.rentalGroups.length
}

let cachedCount = countItems(readOrderDraft())
const listeners = new Set<() => void>()

function notify(nextCount: number): void {
  if (nextCount === cachedCount) return
  cachedCount = nextCount
  for (const l of listeners) l()
}

export function writeOrderDraft(values: OrderDraftValues): void {
  try {
    if (isBlankDraft(values)) {
      window.localStorage.removeItem(ORDER_DRAFT_KEY)
    } else {
      window.localStorage.setItem(
        ORDER_DRAFT_KEY,
        JSON.stringify({ version: ORDER_DRAFT_VERSION, savedAt: Date.now(), values }),
      )
    }
  } catch {
    // Storage full or disabled: the draft just won't survive a crash.
  }
  notify(countItems(isBlankDraft(values) ? null : values))
}

export function clearOrderDraft(): void {
  try {
    window.localStorage.removeItem(ORDER_DRAFT_KEY)
  } catch {
    // Same as above — nothing to recover from here.
  }
  notify(0)
}

export function getOrderDraftItemCount(): number {
  return cachedCount
}

export function subscribeOrderDraft(callback: () => void): () => void {
  listeners.add(callback)
  const onStorage = (e: StorageEvent) => {
    if (e.key === ORDER_DRAFT_KEY || e.key === null) {
      cachedCount = countItems(readOrderDraft())
      callback()
    }
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(callback)
    window.removeEventListener('storage', onStorage)
  }
}

export function useOrderDraftCount(): number {
  return useSyncExternalStore(subscribeOrderDraft, getOrderDraftItemCount)
}
